import { DataGrid, GridActionsCellItem, GridColDef, GridDeleteIcon, GridValueFormatter, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { COMPANY_IN_CHARGE_TYPE, contentFields, coverFields, defaultContentForm, EstimateDto, REMARK_JINYOUNG, REMARK_MUN } from '../../contracts/estimates/types.ts';
import React, { useEffect, useMemo, useState } from 'react';
import { useEstimateApi } from '../../hooks/useEstimateApi.ts';
import ConfirmDialog from './ConfirmDialog.tsx';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { FormProvider, useForm } from 'react-hook-form';
import { Box, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { Button } from 'reactstrap';
import { styles } from '../../contracts/estimateStyle.tsx';
import { ItemsEditor } from './ItemEditor.tsx';
import dayjs from 'dayjs';
import PdfPreviewDialog from '../../components/common/PdfPreviewDialog.tsx';
import { fmtDateTime } from '../../utils/dateFormat.ts';

type Row = {
  id: string;
  companyInCharge?: string;
  contentTitle: string;
  companyName: string;
  recipientName: string;
  estimateDate: string;
  contentType: string;
  createdTimestamp: string;
  total: number;
  raw: EstimateDto;
};

interface EstimatesListProps{
  data: EstimateDto[];
  total: number;
  isLoading: boolean;
  paginationModel: GridPaginationModel;       // { page (0-based), pageSize }
  onPaginationModelChange: (m: GridPaginationModel) => void;
  sortModel: GridSortModel;
  onSortModelChange: (m: GridSortModel) => void;
  onRefresh?: () => Promise<void> | void;
}

// (선택) utils
const fmtThousand = (v: unknown) => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n.toLocaleString('ko-KR') : '';
};


export default function EstimatesList({
  data,
  total,
  isLoading,
  paginationModel,
  onPaginationModelChange,
  sortModel,
  onSortModelChange,
  onRefresh,
}: EstimatesListProps) {
  const { deleteEstimate } = useEstimateApi();

  const rows: Row[] = useMemo(
    () =>
      data.map((e) => ({
        id: e.id,
        companyInCharge: e.companyInCharge,
        contentTitle: e.content.contentTitle,
        companyName: e.cover.companyName,
        recipientName: e.cover.recipientName,
        estimateDate: e.cover.estimateDate,
        contentType: e.content.contentType,
        createdTimestamp: e.createdTimestamp,
        total: e.content.items.reduce((sum, i) => {
          const amount = Number(i.amount) || 0
          return sum + i.amount
        }, 0),
        raw: e,
      })),
    [data]
  );

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [targetId, setTargetId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // ✅ 커뮤니티 대체: grid 아래에 펼쳐질 대상 row만 추적
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const expandedRow = useMemo(
    () => rows.find(r => r.id === expandedId) || null,
    [rows, expandedId]
  );

  const askDelete = (id: string) => {
    setTargetId(id);
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (!targetId) return;
    try {
      setSubmitting(true);
      await deleteEstimate(targetId);
      setConfirmOpen(false);
      setTargetId(null);
      if (onRefresh) await onRefresh();
      else window.location.reload();
    } catch (e: any) {
      alert(e.message ?? "삭제 중 오류");
    } finally {
      setSubmitting(false);
    }
  };

  // 리스트 컴포넌트 내부 state 추가
  const [detailOpen, setDetailOpen] = useState(false);
  const [editing, setEditing] = useState<EstimateDto | null>(null);

  const openDetail = (dto: EstimateDto) => { setEditing(dto); setDetailOpen(true); };
  const closeDetail = () => { setDetailOpen(false); setEditing(null); };

  const columns: GridColDef[] = [
    { field: "contentType", headerName: "구분", width: 80, }, 
    { field: "companyInCharge", headerName: "공급업체", width: 80, }, 
    { field: "contentTitle", headerName: "견적서 제목", flex: 1, },  
    { field: "companyName", headerName: "업체명", flex: 1, },  
    { field: "recipientName", headerName: "수신자", flex: 1, }, 
    { field: "estimateDate", headerName: "견적일", flex: 1, }, 
    {
      field: 'total',
      headerName: '합계(₩)',
      minWidth: 140,
      type: 'number',            // 정렬을 숫자로
      align: 'right',
      headerAlign: 'right',
      renderCell: ({ value }) => <>{fmtThousand(value)}</>,
      // valueFormatter: ({ value }) => fmtThousand(value),
    },
    {
      field: "createdTimestamp",
      headerName: "작성일자",
      flex: 1,
      renderCell: ({ value }) => <>{fmtDateTime(value)}</>,
    },
    {
      field: "actions",
      type: "actions",
      width: 100,
      getActions: (params) => [
          <GridActionsCellItem
            key="items"
            icon={<ExpandMoreIcon />}
            label="항목 편집"
            onClick={() => openDetail((params.row as Row).raw)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<GridDeleteIcon />}
            label="Delete"
            onClick={() => askDelete(String(params.id))}
          />
        ]
      },
  ];

  return (
    <>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(r) => r.id}
        loading={isLoading}
        autoHeight
        pageSizeOptions={[20, 50, 100]}
        paginationMode="server"
        rowCount={total}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        disableRowSelectionOnClick
      />

      <DetailDialog
        open={detailOpen}
        estimate={editing}
        onClose={closeDetail}
        onSaved={async () => {
          if (onRefresh) await onRefresh();    // 저장 후 목록 갱신
        }}
      />


      <ConfirmDialog
        open={confirmOpen}
        title="정말 지우시겠습니까?"
        message="이 작업은 되돌릴 수 없습니다."
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        loading={submitting}
        confirmText="예"
        cancelText="아니오"
      />
    </>
  );
}

function DetailDialog({
  open,
  estimate,
  onClose,
  onSaved,
}: {
  open: boolean;
  estimate: EstimateDto | null;
  onClose: () => void;
  onSaved?: (e: EstimateDto) => void;
}) {
  if (!estimate) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      keepMounted
      PaperProps={{ sx: { height: '90vh' } }}     // 미리보기/폼 공간 넉넉히
    >
      <DialogTitle>견적 상세</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>     {/* 내부 패딩 제거 */}
        <DetailForm estimate={estimate} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
}

/* -------- 상세 폼 (그대로 재사용) -------- */
function DetailForm({ estimate }: { estimate: EstimateDto }) {
  const methods = useForm<EstimateDto>({ defaultValues: estimate });
  const { register, handleSubmit, reset, formState } = methods;
  const [saverOpen, setSaverOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);

  const { previewFullPdf, updateEstimate,
      previewFullPdfMun
   } = useEstimateApi();

  useEffect(() => { if (estimate) reset(estimate); }, [estimate, reset]);

  const askSave = handleSubmit((data) => {
    if (data.companyInCharge == COMPANY_IN_CHARGE_TYPE[0]){
      const payload: EstimateDto = {
        ...estimate,
        ...data,
        content: {
          ...estimate.content,
          remark: REMARK_JINYOUNG
        },
        modifiedTimestamp: dayjs().tz('Asia/Seoul').format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
      };
      setSaverOpen(true);
      (window as any).__pendingEstimate__ = payload;
    } else {
      const payload: EstimateDto = {
        ...estimate,
        ...data,
        content: {
          ...estimate.content,
          remark: REMARK_MUN
        },
        modifiedTimestamp: dayjs().tz('Asia/Seoul').format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
      };
      setSaverOpen(true);
      (window as any).__pendingEstimate__ = payload;
    }
  });

  const confirmSave = async () => {
    const pending = (window as any).__pendingEstimate__ as EstimateDto | undefined;
    if (!pending) return;
    try {
      setSubmitting(true);
      await updateEstimate(pending);
      reset(pending);
      setSaverOpen(false);
      (window as any).__pendingEstimate__ = undefined;
      window.location.reload();
    } catch (e: any) {
      alert(e.message ?? '저장 중 오류');
    } finally {
      setSubmitting(false);
    }
  };

  const [previewOpen, setPreviewOpen] = useState(false);
  const openPreview = handleSubmit(async (data) => {
    try {
      const payload: EstimateDto = { ...estimate, ...data };
      if (data.companyInCharge == COMPANY_IN_CHARGE_TYPE[0]){
        const blob = await previewFullPdf(payload);
        setPdfBlob(blob);
        setPreviewOpen(true);
      } else {
        const blob = await previewFullPdfMun(payload);
        setPdfBlob(blob);
        setPreviewOpen(true);
      }
    } catch (e: any) {
      alert(e?.message ?? '미리보기 실패');
    }
  });

  return (
    <>
      <FormProvider {...methods}>
        <h2 style={styles.largeText}>표지</h2>
        <Box sx={{ p:2, display:'grid', gridTemplateColumns:'1fr 1fr', gap:2 }}>
          {coverFields.map(({ label, name, type }) => (
            <TextField key={name} label={label} type={type} {...register(`cover.${name}`)} />
          ))}
        </Box>

        <h2 style={styles.largeText}>본문</h2>
        <Box sx={{ p:2, display:'grid', gridTemplateColumns:'1fr 1fr', gap:2 }}>
          {contentFields.map(({ label, name, type }) => (
            <TextField key={name} label={label} type={type} {...register(`content.${name}`)} />
          ))}
        </Box>

        <ItemsEditor />

        <div style={{ display:'flex', justifyContent:'flex-end', gap:8 }}>
          <Button onClick={openPreview}>미리보기</Button>
          <Button onClick={askSave} disabled={!formState.isDirty}>저장</Button>
        </div>
      </FormProvider>

      <PdfPreviewDialog
        estimateDto={methods.getValues()}
        open={previewOpen}
        blob={pdfBlob}
        onClose={() => setPreviewOpen(false)}
        fileName={`estimate-${estimate?.cover.estimateDate || 'preview'}.pdf`}
      />

      <ConfirmDialog
        open={saverOpen}
        title="수정하시겠습니까?"
        message="이 작업은 되돌릴 수 없습니다."
        onClose={() => setSaverOpen(false)}
        onConfirm={confirmSave}
        loading={submitting}
        confirmText="예"
        cancelText="아니오"
      />
    </>
  );
}
